import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const drift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-18px, 14px) scale(1.06); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pillPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(34, 211, 238, 0.55); }
  70%  { box-shadow: 0 0 0 8px rgba(34, 211, 238, 0); }
  100% { box-shadow: 0 0 0 0 rgba(34, 211, 238, 0); }
`;

export const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 95vh;
  overflow: hidden;
`;

/* Duas manchas de luz que respiram bem devagar ao fundo, atrás do
   conteúdo -- reforça a leitura "central de comando" sem competir com os
   cards. */
export const AmbientGlow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    filter: blur(90px);
    opacity: 0.35;
    animation: ${drift} 16s ease-in-out infinite;
  }

  &::before {
    top: -120px;
    right: -100px;
    background: radial-gradient(circle, rgba(43, 123, 255, 0.55), transparent 70%);
  }

  &::after {
    bottom: -140px;
    left: -80px;
    background: radial-gradient(circle, rgba(34, 211, 238, 0.4), transparent 70%);
    animation-delay: -8s;
  }
`;

export const MainContent = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  padding: 28px 32px 44px;
  overflow-y: auto;
`;

/* ------------------------------- Header ------------------------------- */

export const Header = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease both;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  span.eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--accent-3);
  }

  span.eyebrow::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--gradient-cyan);
    box-shadow: 0 0 10px rgba(34, 211, 238, 0.8);
  }

  h1 {
    margin: 0;
    font-family: var(--font-display);
    font-size: 2.3rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--text);
    line-height: 1.08;
  }

  h1 .accent {
    background: var(--gradient-brand);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  p {
    margin: 6px 0 0;
    max-width: 46ch;
    color: var(--text-muted);
    font-size: 0.98rem;
  }
`;

/* ---------------------------- Section label ---------------------------- */

export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0 16px;
  animation: ${fadeUp} 0.5s ease both;

  h3 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 9px;
    font-family: var(--font-display);
    font-size: 1.05rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--text);
  }

  svg {
    color: var(--accent);
    font-size: 1.1rem;
  }

  small {
    color: var(--text-dim);
    font-weight: 500;
    font-size: 0.78rem;
  }

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, var(--border), transparent);
  }
`;

/* ------------------------------ Tool grid ------------------------------ */

export const ToolGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 18px;
  margin-bottom: 34px;
`;

export const ToolCard = styled.button<{ $accent: string; $delay?: number; disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  text-align: left;
  padding: 22px;
  border-radius: 20px;
  border: 1px solid var(--border);
  background: var(--surface);
  box-shadow: var(--shadow);
  cursor: ${(p) => (p.disabled ? "wait" : "pointer")};
  opacity: ${(p) => (p.disabled ? 0.7 : 1)};
  overflow: hidden;
  isolation: isolate;
  font-family: inherit;
  animation: ${fadeUp} 0.5s ease both;
  animation-delay: ${(p) => (p.$delay ?? 0) * 70}ms;
  transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease,
    border-color 0.25s ease;

  /* Anel de luz que só acende no hover -- a "moldura futurista". */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    padding: 1.5px;
    border-radius: inherit;
    background: linear-gradient(135deg, ${(p) => p.$accent}, transparent 60%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.25s ease;
  }

  /* Textura de "circuito" bem sutil, só perceptível de perto. */
  &::after {
    content: "";
    position: absolute;
    top: -40%;
    right: -20%;
    width: 60%;
    height: 180%;
    background: radial-gradient(circle, ${(p) => p.$accent}22, transparent 65%);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);
    border-color: transparent;
  }

  &:hover:not(:disabled)::before,
  &:hover:not(:disabled)::after {
    opacity: 1;
  }

  &:active:not(:disabled) {
    transform: translateY(-2px);
  }
`;

export const ToolIcon = styled.div<{ $accent: string }>`
  flex-shrink: 0;
  width: 54px;
  height: 54px;
  border-radius: 15px;
  display: grid;
  place-items: center;
  color: #fff;
  background: ${(p) => p.$accent};
  box-shadow: 0 8px 20px ${(p) => p.$accent}55;

  svg {
    font-size: 26px;
  }
`;

export const ToolBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;

  .title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  h2 {
    margin: 0;
    font-family: var(--font-display);
    font-size: 1.08rem;
    font-weight: 700;
    color: var(--text);
    letter-spacing: -0.01em;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.45;
    color: var(--text-muted);
  }

  .meta {
    margin-top: 6px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: fit-content;
    padding: 3px 10px;
    border-radius: 999px;
    background: var(--surface-2);
    border: 1px solid var(--border);
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-dim);
  }
`;

export const NewPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--gradient-cyan);
  color: #041b23;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  animation: ${pillPulse} 2.4s ease-in-out infinite;
`;

export const ToolArrow = styled.div`
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  margin-top: 2px;
  border-radius: 50%;
  color: var(--text-dim);
  background: var(--surface-2);
  transition: transform 0.25s ease, color 0.25s ease, background 0.25s ease;

  ${ToolCard}:hover & {
    transform: translateX(3px);
    color: #fff;
    background: var(--gradient-brand);
  }
`;
